import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useFormContext, UseFormReturn } from "react-hook-form";

interface UnsavedChangesContextValue {
  unsavedForms: {
    [key: string]: boolean;
  };
  hasUnsavedChanges: (formId: string) => boolean;
  setHasUnsavedChanges: (formId: string, hasChanges: boolean) => void;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextValue | null>(
  null,
);

export function useUnsavedChangesContext() {
  const context = useContext(UnsavedChangesContext);
  if (!context) {
    throw new Error(
      "useUnsavedChangesContext must be used within a UnsavedChangesProvider",
    );
  }
  return context;
}

interface UnsavedChangesProviderProps {
  children: ReactNode;
}

export function UnsavedChangesProvider({
  children,
}: UnsavedChangesProviderProps) {
  const [unsavedForms, setUnsavedForms] = useState<{
    [key: string]: boolean;
  }>({});

  const hasUnsavedChanges = useCallback(
    (formId: string) => {
      return unsavedForms[formId] ?? false;
    },
    [unsavedForms],
  );

  const setHasUnsavedChanges = useCallback(
    (formId: string, hasChanges: boolean) => {
      setUnsavedForms((prev) => ({
        ...prev,
        [formId]: hasChanges,
      }));
    },
    [],
  );

  return (
    <UnsavedChangesContext.Provider
      value={{
        unsavedForms,
        hasUnsavedChanges,
        setHasUnsavedChanges,
      }}
    >
      {children}
    </UnsavedChangesContext.Provider>
  );
}

export function useUnsavedChangesRegister(
  formId: string,
  formProp?: Pick<UseFormReturn, "formState">,
) {
  const { setHasUnsavedChanges } = useUnsavedChangesContext();
  const formContext = useFormContext();
  const form = formProp ?? formContext;

  useEffect(() => {
    if (form) setHasUnsavedChanges(formId, form.formState.isDirty);
    return () => setHasUnsavedChanges(formId, false);
  }, [form.formState.isDirty, setHasUnsavedChanges, form, formId]);
}
